import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type AnimationPlaybackControls,
} from "framer-motion";
import { useDrag } from "@use-gesture/react";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Info,
  Loader2,
  Pause,
  Play,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useToastStore,
  type Toast as ToastModel,
  type ToastAction,
  type ToastVariant,
} from "../store/toast-store";

interface ToastProps {
  toast: ToastModel;
}

const ICONS: Record<
  ToastVariant,
  React.ComponentType<{ className?: string }>
> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
  loading: Loader2,
};

/**
 * Varyant renkleri Tailwind paletinden geliyor: info ve loading'in semantic
 * token karşılığı yok, dört varyantı tek bir kaynakta tutmak için hepsi palet
 * üzerinden tanımlı. Nötr yüzey renkleri ise token kullanıyor.
 */
const VARIANT_STYLES: Record<
  ToastVariant,
  { icon: string; chip: string; progress: string }
> = {
  success: {
    icon: "text-green-600 dark:text-green-400",
    chip: "bg-green-500/10 dark:bg-green-400/10",
    progress: "bg-green-500 dark:bg-green-400",
  },
  error: {
    icon: "text-red-600 dark:text-red-400",
    chip: "bg-red-500/10 dark:bg-red-400/10",
    progress: "bg-red-500 dark:bg-red-400",
  },
  warning: {
    icon: "text-amber-600 dark:text-amber-400",
    chip: "bg-amber-500/10 dark:bg-amber-400/10",
    progress: "bg-amber-500 dark:bg-amber-400",
  },
  info: {
    icon: "text-blue-600 dark:text-blue-400",
    chip: "bg-blue-500/10 dark:bg-blue-400/10",
    progress: "bg-blue-500 dark:bg-blue-400",
  },
  loading: {
    icon: "text-blue-600 dark:text-blue-400",
    chip: "bg-blue-500/10 dark:bg-blue-400/10",
    progress: "bg-blue-500 dark:bg-blue-400",
  },
};

/** Sürükleyerek kapatma için aşılması gereken mesafe (px) */
const DISMISS_DISTANCE = 96;
/** Bu hızın üzerindeki savurmalar mesafeye bakılmadan kapatır */
const DISMISS_VELOCITY = 0.4;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * useDrag yalnızca pointer/touch handler'ları döndürür. Tipi bu alt kümeye
 * daraltmak, framer-motion'ın kendi onDrag/onAnimationStart imzalarıyla
 * çakışmayı `any` kullanmadan çözüyor.
 */
type GestureHandlers = Pick<
  React.DOMAttributes<HTMLDivElement>,
  | "onPointerDown"
  | "onPointerMove"
  | "onPointerUp"
  | "onPointerCancel"
  | "onGotPointerCapture"
  | "onLostPointerCapture"
  | "onTouchStart"
  | "onTouchMove"
  | "onTouchEnd"
  | "onMouseDown"
>;

export const Toast: React.FC<ToastProps> = ({ toast }) => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  // Selector ile okuma: store state'i değiştiğinde bu bileşen render edilmez.
  const removeToast = useToastStore((state) => state.removeToast);

  const { duration, id } = toast;
  const isTimed = duration > 0;
  const variant = VARIANT_STYLES[toast.type];
  const Icon = ICONS[toast.type];

  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [isPointerOver, setIsPointerOver] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const isPaused = isManuallyPaused || isPointerOver || isFocused || isDragging;

  // Sayaç tamamen bileşen içinde: store'a yazmadığı için geri kalan uygulamayı
  // yeniden render etmiyor. İlerleme bir motion value olduğu için bu bileşen de
  // sayaç boyunca hiç render almıyor.
  const progress = useMotionValue(100);
  const progressScale = useTransform(progress, [0, 100], [0, 1]);
  const remainingRef = useRef(duration);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const opacity = useMotionValue(1);
  const scale = useMotionValue(1);
  const rotate = useMotionValue(0);

  const dismiss = useCallback(() => removeToast(id), [removeToast, id]);

  // updatedAt değişimi = toast tazelendi (dedupe veya promise sonucu): sayacı sıfırla
  useEffect(() => {
    remainingRef.current = duration;
    progress.set(100);
  }, [duration, toast.updatedAt, progress]);

  useEffect(() => {
    if (!isTimed || isPaused) return;

    let frame = 0;
    const startedAt = performance.now();
    const startRemaining = remainingRef.current;

    const tick = (now: number) => {
      const remaining = startRemaining - (now - startedAt);
      remainingRef.current = Math.max(0, remaining);
      progress.set((remainingRef.current / duration) * 100);

      if (remaining <= 0) {
        dismiss();
        return;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [isTimed, isPaused, duration, toast.updatedAt, progress, dismiss]);

  // Yeni bir sürükleme başladığında devam eden geri-yerleşme animasyonu
  // motion value'ları geri çekmeye çalışmasın diye durduruluyor.
  const runningAnimations = useRef<AnimationPlaybackControls[]>([]);

  const stopAnimations = useCallback(() => {
    runningAnimations.current.forEach((animation) => animation.stop());
    runningAnimations.current = [];
  }, []);

  const settleBack = useCallback(() => {
    const transition = { duration: 0.18, ease: "easeOut" } as const;
    runningAnimations.current = [
      animate(x, 0, transition),
      animate(y, 0, transition),
      animate(opacity, 1, transition),
      animate(scale, 1, transition),
      animate(rotate, 0, transition),
    ];
  }, [x, y, opacity, scale, rotate]);

  const dismissByGesture = useCallback(
    (movementX: number, movementY: number) => {
      const transition = { duration: 0.18, ease: "easeOut" } as const;
      const flyOut = (movement: number) =>
        movement === 0
          ? 0
          : Math.sign(movement) * Math.max(Math.abs(movement) * 1.6, 240);

      runningAnimations.current = [
        animate(x, flyOut(movementX), transition),
        animate(y, flyOut(movementY), transition),
        animate(opacity, 0, { ...transition, onComplete: dismiss }),
      ];
    },
    [x, y, opacity, dismiss],
  );

  // touch-action: pan-y sayesinde dikey kaydırma tarayıcıda kalır; dokunmatikte
  // yalnızca yatay savurma bize gelir, mouse ile her iki eksen çalışır.
  const bindDrag = useDrag(
    ({
      down,
      first,
      last,
      movement: [movementX, movementY],
      velocity: [velocityX],
      direction: [directionX],
    }) => {
      if (!toast.dismissible) return;

      if (first) {
        stopAnimations();
        setIsDragging(true);
      }

      if (down) {
        x.set(movementX);
        y.set(movementY);

        const travelled = Math.hypot(movementX, movementY);
        opacity.set(clamp(1 - travelled / 260, 0.3, 1));
        scale.set(clamp(1 - travelled / 1400, 0.95, 1));
        if (!prefersReducedMotion) {
          rotate.set(clamp(movementX * 0.035, -5, 5));
        }
        return;
      }

      if (!last) return;
      setIsDragging(false);

      const flung = Math.abs(velocityX) > DISMISS_VELOCITY && directionX !== 0;
      const travelled = Math.max(Math.abs(movementX), Math.abs(movementY));

      if (flung || travelled > DISMISS_DISTANCE) {
        dismissByGesture(movementX, movementY);
      } else {
        settleBack();
      }
    },
    {
      filterTaps: true,
      bounds: { left: -260, right: 260, top: -260, bottom: 260 },
      rubberband: 0.15,
      pointer: { touch: true },
    },
  );

  const runAction = (action: ToastAction) => {
    action.action();
    dismiss();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && toast.dismissible) {
      event.stopPropagation();
      dismiss();
    }
  };

  const enterExit = prefersReducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
      }
    : {
        initial: { opacity: 0, y: 16, scale: 0.96 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, scale: 0.96 },
      };

  return (
    <motion.li
      layout={!prefersReducedMotion}
      {...enterExit}
      transition={{ duration: 0.18, ease: "easeOut" }}
      className="pointer-events-auto list-none"
    >
      {/* Sürükleme yüzeyi: enter/exit animasyonlarıyla çakışmaması için
          transform'lar ayrı katmanda tutuluyor. */}
      <motion.div
        // touchAction: pan-y dikey kaydırmayı tarayıcıya bırakır
        style={{ x, y, opacity, scale, rotate, touchAction: "pan-y" }}
        {...(bindDrag() as GestureHandlers)}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onPointerEnter={(event) => {
          if (event.pointerType !== "touch") setIsPointerOver(true);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType !== "touch") setIsPointerOver(false);
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        className={cn(
          "group relative flex w-full items-start gap-3 overflow-hidden rounded-xl p-4",
          "border border-border-secondary bg-bg-primary/95 backdrop-blur-xl",
          "shadow-lg shadow-black/5 dark:shadow-black/40",
          "outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
          toast.dismissible && "cursor-grab active:cursor-grabbing",
        )}
      >
        <div
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-full",
            variant.chip,
          )}
        >
          <Icon
            className={cn(
              "h-4 w-4",
              variant.icon,
              toast.type === "loading" && "animate-spin",
            )}
          />
        </div>

        <div className="min-w-0 flex-1">
          {toast.title && (
            <div className="mb-0.5 text-sm font-medium leading-tight text-text-primary">
              {toast.title}
            </div>
          )}

          <div className="text-sm leading-snug break-words text-text-secondary">
            {/* HTML yalnızca çağıran taraf açıkça istediğinde render edilir;
                backend mesajları ve kullanıcı girdisi düz metin kalır. */}
            {toast.html ? (
              <span dangerouslySetInnerHTML={{ __html: toast.message }} />
            ) : (
              toast.message
            )}
          </div>

          {typeof toast.progress === "number" && (
            <div className="mt-2">
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-border-secondary">
                <div
                  className={cn(
                    "h-full rounded-full transition-all",
                    variant.progress,
                  )}
                  style={{ width: `${clamp(toast.progress, 0, 100)}%` }}
                />
              </div>
              <div className="mt-1 text-xs text-text-tertiary">
                {Math.round(toast.progress)}%
              </div>
            </div>
          )}

          {toast.actions && toast.actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {toast.actions.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => runAction(action)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
                    action.style === "primary" &&
                      "bg-primary text-primary-foreground hover:bg-primary/90",
                    action.style === "destructive" &&
                      "bg-red-500/10 text-red-600 hover:bg-red-500/20 dark:text-red-400",
                    (!action.style || action.style === "secondary") &&
                      "bg-bg-secondary text-text-secondary hover:bg-bg-tertiary",
                  )}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="ml-1 flex shrink-0 flex-col gap-1">
          {isTimed && (
            <button
              type="button"
              onClick={() => setIsManuallyPaused((paused) => !paused)}
              aria-label={
                isManuallyPaused
                  ? t("toast.resumeTimer")
                  : t("toast.pauseTimer")
              }
              className="rounded-full p-1 text-text-tertiary opacity-0 transition-opacity hover:bg-bg-secondary focus-visible:opacity-100 group-hover:opacity-100"
            >
              {isManuallyPaused ? (
                <Play className="h-3 w-3" />
              ) : (
                <Pause className="h-3 w-3" />
              )}
            </button>
          )}

          {toast.dismissible && (
            <button
              type="button"
              onClick={dismiss}
              aria-label={t("common.close")}
              className="rounded-full p-1 text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        {isTimed && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-border-secondary">
            <motion.div
              className={cn("h-full origin-left", variant.progress)}
              style={{ scaleX: progressScale }}
            />
          </div>
        )}
      </motion.div>
    </motion.li>
  );
};
